import { Avatar, FlexBox, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { getLoginURL } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import type { Organization } from 'lib-shared-types-api';
import { PropsWithChildren, PropsWithClassName } from 'lib-shared-types-utils';

import { loginApi } from 'apis/login';
import { emulateLinkInteraction } from 'app/utils/emulateLinkInteraction/emulateLinkInteraction';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from './organizationsNavigation.module.scss';

/** Represents the props of the OrganizationsNavigation component. */
type OrganizationsNavigationProps = PropsWithChildren & PropsWithClassName;

/**
 * Represents the component where organizations available to the user are
 * displayed and can be navigated between.
 */
export const OrganizationsNavigation = ({
  className,
}: OrganizationsNavigationProps): JSX.Element => {
  const [createLoginToken] = loginApi.useCreateLoginTokenMutation();
  const organizations = useAppSelector((state) => state.app.organizations);
  const currentOrganization = useAppSelector((state) => state.app.organization);

  const openOrganization = async (
    event:
      | React.KeyboardEvent<HTMLOrSVGElement>
      | React.MouseEvent<HTMLOrSVGElement, MouseEvent>,
    organization: Organization,
  ) => {
    ampli.organizationsNavigationOrganizationClicked({
      id: organization.id,
      name: organization.name,
      subdomain: organization.subdomain,
    });

    const { data: loginTokenData } = await createLoginToken().unwrap();

    const navigateToUrl = getLoginURL({
      loginToken: loginTokenData.login_token,
      organization: organization,
      url: new URL(window.location.href),
    });

    emulateLinkInteraction(navigateToUrl, event);
  };

  const handleOnKeyDown = (
    event: React.KeyboardEvent<HTMLOrSVGElement>,
    organization: Organization,
  ) => {
    if (event.key === 'Enter') {
      openOrganization(event, organization);
    }
  };

  const handleOnClick = (
    event: React.MouseEvent<HTMLOrSVGElement, MouseEvent>,
    organization: Organization,
  ) => openOrganization(event, organization);

  const rootClass = classnames(styles.root, className);

  return (
    <FlexBox
      as="nav"
      className={rootClass}
      aria-label="Organizations"
      padding="200 250"
      flexDirection="column"
      display="flex"
      gap="250"
    >
      {organizations?.map((organization) => (
        <Tooltip key={organization.id} content={organization.name} side="right">
          <FlexBox
            tabIndex={0}
            onClick={(event) => handleOnClick(event, organization)}
            onKeyDown={(event) => handleOnKeyDown(event, organization)}
            role="link"
            className={classnames(
              {
                [styles.active]: currentOrganization?.id === organization.id,
              },
              styles.item,
            )}
          >
            <Avatar
              size="24"
              className={styles.avatar}
              user={{
                id: organization.id,
                avatar_url: organization.avatar_url,
                name: organization.name,
              }}
            />
          </FlexBox>
        </Tooltip>
      ))}
    </FlexBox>
  );
};
